import React, { useState, useEffect } from "react";
import { bookDemoApi } from "../Utils/Axios";

const BookDemoModal = ({ onClose }) => {
  const [form, setForm] = useState({ fullName: "", email: "", phone: "", message: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = "Name is required";
    else if (form.fullName.trim().length < 2) e.fullName = "Minimum 2 characters";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/.test(form.email))
      e.email = "Invalid email format";
    if (form.phone && !/^\d{10}$/.test(form.phone.trim()))
      e.phone = "Invalid phone number (10 digits required)";
    if (form.message.length > 150) e.message = "Max 150 characters";
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      const onlyNums = value.replace(/[^0-9]/g, "");
      if (onlyNums.length > 10) return;
      setForm((f) => ({ ...f, [name]: onlyNums }));
    } else {
      setForm((f) => ({ ...f, [name]: value }));
    }
    if (errors[name]) setErrors((er) => ({ ...er, [name]: "" }));
    setServerError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try {
      await bookDemoApi({
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        message: form.message.trim() || null,
      });
      setSuccess(true);
    } catch (err) {
      const msg = err?.response?.data?.error?.message || "Something went wrong. Please try again.";
      setServerError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bdm-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bdm-card">

        {/* Header bar */}
        <div className="bdm-card-header">
          <div className="bdm-card-header-left">
            <span className="bdm-card-icon"><i className="ph-fill ph-calendar-check" /></span>
            <div>
              <h2 className="bdm-title">Book a Free Demo</h2>
              <p className="bdm-subtitle">See CUB HRM live — tailored to your team</p>
            </div>
          </div>
          <button className="bdm-close" onClick={onClose} aria-label="Close">&#10005;</button>
        </div>

        {success ? (
          <div className="bdm-success">
            <div className="bdm-success-icon"><i className="ph-fill ph-check-circle" /></div>
            <h3 className="bdm-success-title">Request received!</h3>
            <p className="bdm-success-msg">
              Our team will contact you within <strong>1–2 business days</strong> to schedule your personalised demo.
            </p>
            <p className="bdm-success-sub">
              A confirmation has been sent to <strong>{form.email}</strong>
            </p>
            <button className="bdm-submit-btn" onClick={onClose} style={{ marginTop: "8px" }}>
              Done
            </button>
          </div>
        ) : (
          <form className="bdm-form" onSubmit={handleSubmit} noValidate>
            <div className="bdm-row">
              <div className="bdm-field">
                <label className="bdm-label">Full Name <span className="bdm-req">*</span></label>
                <input
                  className={`bdm-input${errors.fullName ? " bdm-input--error" : ""}`}
                  name="fullName" value={form.fullName} onChange={handleChange}
                  placeholder="Your full name" autoComplete="name"
                />
                {errors.fullName && <p className="bdm-error">{errors.fullName}</p>}
              </div>
              <div className="bdm-field">
                <label className="bdm-label">Work Email <span className="bdm-req">*</span></label>
                <input
                  className={`bdm-input${errors.email ? " bdm-input--error" : ""}`}
                  name="email" type="email" value={form.email} onChange={handleChange}
                  placeholder="you@company.com" autoComplete="email"
                />
                {errors.email && <p className="bdm-error">{errors.email}</p>}
              </div>
            </div>

            <div className="bdm-field">
              <label className="bdm-label">
                Phone
                <span className="bdm-optional"> (optional)</span>
              </label>
              <input
                className={`bdm-input${errors.phone ? " bdm-input--error" : ""}`}
                name="phone" value={form.phone} onChange={handleChange}
                placeholder="9876543210" autoComplete="tel" maxLength="10"
              />
              {errors.phone && <p className="bdm-error">{errors.phone}</p>}
            </div>

            <div className="bdm-field">
              <label className="bdm-label">
                Message
                <span className="bdm-optional"> (optional)</span>
                <span className="bdm-char-count">{form.message.length}/150</span>
              </label>
              <textarea
                className={`bdm-input bdm-textarea${errors.message ? " bdm-input--error" : ""}`}
                name="message" value={form.message} onChange={handleChange}
                placeholder="Team size, what you'd like to see..."
                maxLength={150}
              />
              {errors.message && <p className="bdm-error">{errors.message}</p>}
            </div>

            {serverError && <p className="bdm-server-error">{serverError}</p>}

            <button className="bdm-submit-btn" type="submit" disabled={loading}>
              {loading && <span className="bdm-spinner" />}
              {loading ? "Submitting..." : "Book My Demo"}
            </button>

            <p className="bdm-privacy">
              <i className="ph ph-lock-simple" /> Your information is kept confidential.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default BookDemoModal;
